import {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {deleteSenior, getSeniorDetail, updateSenior} from "../../../api/seniorApi";
import {SeniorFormData} from "../../../interface/SeniorFormData";
import {useFormValidation} from "../../../hooks/useFormValidation";
import {defaultAlert} from "../../../utils/alert";
import useIosInputAutoScroll from "../../../hooks/useIosInputAutoScroll";

export default function SeniorDetailPage() {
    const {seniorCode} = useParams();
    const navigate = useNavigate();
    const {validateSeniorFormPage} = useFormValidation();
    useIosInputAutoScroll();
    const [formData, setFormData] = useState<SeniorFormData>({
        seniorCode: "",
        name: "",
        birthday: "",
        gender: "",
        weight: "",
        height: "",
        longTermCareInsuranceNumber: "",
        relationship: "",
    });


    useEffect(() => {
        if (!seniorCode) return;
        const fetchSeniorDetail = async () => {
            try {
                const res = await getSeniorDetail(seniorCode);
                setFormData(res.data.data);
            } catch (error) {
                console.error("Failed to fetch senior detail:", error);
            }
        };
        fetchSeniorDetail();
    }, [seniorCode]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const {name, value} = e.target;
        let sanitized = value;
        if (name === 'height' || name === 'weight') {
            const num = value.replace(/[^0-9]/g, '');
            sanitized = num === '' ? '' : String(parseInt(num, 10));
        }
        setFormData((prev) => ({
            ...prev,
            [name]: sanitized
        }));
    };


    const handleSubmit = async (e?: React.MouseEvent<HTMLButtonElement>) => {
        e?.preventDefault();
        if (!seniorCode) {
            defaultAlert("잘못된 요청입니다. seniorCode가 존재하지 않습니다.");
            return;
        }
        if (!validateSeniorFormPage(formData)) {
            return;
        }

        try {
            // formData에서 필요한 타입 맞추기
            const payload = {
                ...formData,
                birthday: formData.birthday,
                weight: formData.weight,
                height: formData.height,
            };

            await updateSenior(seniorCode, payload);
            defaultAlert("저장 완료");
        } catch (error) {
            console.error("Failed to update senior:", error);
            defaultAlert("저장에 실패했습니다.");
        }
    };

    const handleDelete = async () => {
        if (!seniorCode) {
            defaultAlert("잘못된 요청입니다. seniorCode가 존재하지 않습니다.");
            return;
        }
        try {
            await deleteSenior(seniorCode);
            defaultAlert("삭제 완료");
            navigate("/menu/seniorList", {replace: true});
        } catch (error) {
            console.error("Failed to delete senior:", error);
            defaultAlert("삭제에 실패했습니다.");
        }
    };
    const getGenderDisplay = (gender: string) => {
        return gender === 'M' ? '남성' : gender === 'F' ? '여성' : '';
    };

    return (
        <>
            <div className="bottom-btn">
                <button id="" type="button" className="btn cancel" onClick={handleDelete}>삭제</button>
                <button
                    id=""
                    type="button"
                    className="btn confirm"
                    onClick={handleSubmit}
                >
                    저장
                </button>
            </div>
            <main className="container type_gray">
                <div className="form-box">
                    <fieldset>
                        <legend>어르신 정보 폼</legend>
                        <section>
                            <div className="inner-box">
                                <div className="round-box">
                                    <div className="round-header">
                                        <h3 className="round-tit">어르신 정보</h3>
                                    </div>
                                    <div className="round-body">
                                        <div className="form-item">
                                            <label htmlFor="name" className="input-tit">어르신 이름 <span
                                                className="point">*</span></label>
                                            <div className="line-input-box">
                                                <input
                                                    type="text"
                                                    id="name"
                                                    name="name"
                                                    className="text-right"
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-item">
                                            <label htmlFor="birthday" className="input-tit">생년월일 <span
                                                className="point">*</span></label>
                                            <div className="line-input-box">
                                                <input
                                                    type="text"
                                                    id="birthday"
                                                    name="birthday"
                                                    className="text-right"
                                                    value={formData.birthday}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-item">
                                            <label htmlFor="gender" className="input-tit">성별</label>
                                            <div className="line-input-box">
                                                <select
                                                    id="gender"
                                                    name="gender"
                                                    className="text-right"
                                                    value={formData.gender}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">선택하세요</option>
                                                    <option value="M">남성</option>
                                                    <option value="F">여성</option>
                                                </select>

                                            </div>
                                        </div>
                                        <div className="form-item">
                                            <label htmlFor="height" className="input-tit">키(cm)</label>
                                            <div className="line-input-box">
                                                <input
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    type="text"
                                                    id="height"
                                                    name="height"
                                                    className="text-right"
                                                    value={formData.height}
                                                    onChange={handleInputChange}
                                                />
                                                <span>cm</span>
                                            </div>
                                        </div>
                                        <div className="form-item">
                                            <label htmlFor="weight" className="input-tit">체중(Kg)</label>
                                            <div className="line-input-box">
                                                <input
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    type="text"
                                                    id="weight"
                                                    name="weight"
                                                    className="text-right"
                                                    value={formData.weight}
                                                    onChange={handleInputChange}
                                                />
                                                <span>kg</span>
                                            </div>
                                        </div>
                                        <div className="form-item">
                                            <label htmlFor="longTermCareInsuranceNumber" className="input-tit">장기요양보험 가입번호</label>
                                            <div className="line-input-box">
                                                <input
                                                    type="text"
                                                    id="longTermCareInsuranceNumber"
                                                    name="longTermCareInsuranceNumber"
                                                    className="text-right"
                                                    value={formData.longTermCareInsuranceNumber}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-item">
                                            <label htmlFor="relationship" className="input-tit">관계</label>
                                            <div className="line-input-box">
                                                <select
                                                    id="relationship"
                                                    name="relationship"
                                                    value={formData.relationship}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="" hidden>선택</option>
                                                    <option value="SPOUSE">배우자</option>
                                                    <option value="PARENT">부모</option>
                                                    <option value="CHILD">자녀</option>
                                                    <option value="SIBLING">형제자매</option>
                                                    <option value="COUSIN">친척</option>
                                                    <option value="OTHER">기타</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </fieldset>
                </div>
            </main>
        </>
    );
}
